import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Check, ChevronsUpDown, ArrowLeft } from 'lucide-react';
import { format, addMonths, differenceInCalendarDays } from 'date-fns';
import { cn } from '../lib/utils';
import { toast } from "sonner";

export function CreateSubscriptionScreen() {
    const location = useLocation();
    const navigate = useNavigate();
    const initialState = location.state || {};

    const [userQuery, setUserQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [openUserSelect, setOpenUserSelect] = useState(false);

    // Default end date to 1 month from start or today
    const [endDate, setEndDate] = useState<Date | undefined>(
        initialState.startDate ? addMonths(new Date(initialState.startDate), 1) : addMonths(new Date(), 1)
    );

    // Price state
    const [totalPrice, setTotalPrice] = useState<string>('');
    const [pricePerDay, setPricePerDay] = useState<string>('');

    // Derived state
    const startDate = initialState.startDate ? new Date(initialState.startDate) : new Date();
    const totalDays = endDate ? differenceInCalendarDays(endDate, startDate) + 1 : 0;

    const { data: users } = trpc.searchUsers.useQuery(
        { query: userQuery },
        { enabled: userQuery.length > 0 }
    );

    const createMutation = trpc.createSubscription.useMutation({
        onSuccess: () => {
            toast.success("Subscription created successfully");
            navigate('/seats');
        },
        onError: (err) => {
            toast.error("Failed to create subscription: " + err.message);
        }
    });

    // Update calculations when days change
    useEffect(() => {
        if (totalDays > 0 && pricePerDay) {
            const calculatedTotal = parseFloat(pricePerDay) * totalDays;
            setTotalPrice(calculatedTotal.toFixed(2));
        }
    }, [endDate]); // dependency on endDate which drives totalDays

    const handleTotalPriceChange = (val: string) => {
        setTotalPrice(val);
        if (val && totalDays > 0) {
            const ppd = parseFloat(val) / totalDays;
            setPricePerDay(ppd.toFixed(2));
        } else {
            setPricePerDay('');
        }
    };

    const handlePricePerDayChange = (val: string) => {
        setPricePerDay(val);
        if (val && totalDays > 0) {
            const total = parseFloat(val) * totalDays;
            setTotalPrice(total.toFixed(2));
        } else {
            setTotalPrice('');
        }
    };

    const handleSubmit = () => {
        if (!selectedUser || !endDate || !initialState.branchId) {
            if (!initialState.branchId) {
                toast.error("Invalid session. Return to seats.");
                return;
            }
            toast.error("Please fill all required fields");
            return;
        }

        createMutation.mutate({
            userId: selectedUser.id,
            branchId: initialState.branchId,
            seatId: initialState.seatId,
            shiftId: initialState.shiftId,
            startDate: initialState.startDate,
            endDate: format(endDate, 'yyyy-MM-dd'),
            price: totalPrice ? parseFloat(totalPrice) : undefined
        });
    };

    if (!initialState.branchId) {
        return (
            <div className="p-8 text-center space-y-4">
                <p>Invalid State</p>
                <Button onClick={() => navigate('/seats')}>Go to Seats</Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 pb-20 space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-xl font-bold">New Subscription</h1>
            </div>

            {/* Pre-filled info */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-2 border">
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Seat</span>
                    <span className="font-medium">{initialState.seatNumber}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Shift</span>
                    <span className="font-medium">{initialState.shiftName}</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Start Date</span>
                    <span className="font-medium">{format(startDate, 'd MMM yyyy')}</span>
                </div>
            </div>

            {/* User Select */}
            <div className="space-y-2">
                <label className="text-sm font-medium">User</label>
                <Popover open={openUserSelect} onOpenChange={setOpenUserSelect}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" aria-expanded={openUserSelect} className="w-full justify-between">
                            {selectedUser ? selectedUser.name : "Select user..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                        <Command shouldFilter={false}>
                            <CommandInput placeholder="Search user..." value={userQuery} onValueChange={setUserQuery} />
                            <CommandList>
                                <CommandEmpty>
                                    {userQuery.length === 0 ? "Type to search..." : "No user found."}
                                </CommandEmpty>
                                <CommandGroup>
                                    {users?.map((user) => (
                                        <CommandItem
                                            key={user.id}
                                            value={user.id}
                                            onSelect={() => {
                                                setSelectedUser(user);
                                                setOpenUserSelect(false);
                                            }}
                                        >
                                            <Check className={cn("mr-2 h-4 w-4", selectedUser?.id === user.id ? "opacity-100" : "opacity-0")} />
                                            {user.name}
                                            <span className="ml-auto text-xs text-muted-foreground opacity-50">{user.email}</span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">Search by name or email</p>
            </div>

            {/* End Date */}
            <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <input
                    type="date"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                    min={format(startDate, 'yyyy-MM-dd')}
                    onChange={(e) => setEndDate(e.target.valueAsDate || undefined)}
                />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md text-sm font-medium text-center">
                Total Duration: {totalDays} days
            </div>

            {/* Price Calculations */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">Price Per Day</label>
                    <Input
                        type="number"
                        placeholder="0.00"
                        value={pricePerDay}
                        onChange={(e) => handlePricePerDayChange(e.target.value)}
                    />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Total Price (INR)</label>
                    <Input
                        type="number"
                        placeholder="Optional"
                        value={totalPrice}
                        onChange={(e) => handleTotalPriceChange(e.target.value)}
                    />
                </div>
            </div>

            <Button className="w-full mt-6" size="lg" onClick={handleSubmit} disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Subscription"}
            </Button>
        </div>
    );
}
